import React, { ChangeEventHandler, FocusEventHandler, Fragment } from 'react';
import { createStructuredSelector } from 'reselect';
import { Dispatch } from 'redux';
import { connect } from 'dva';

import TextField from '../TextField/TextField';
import AddressFlat from './AddressFlat/AddressFlat';
import {
  getBuilding,
  getHouse,
  getStreet,
  getStructure,
  isAddressSubregionFieldsVisible,
  isNewMoscow,
  isSubsequentRequestRunning,
  PersonModel,
} from '../../../selectors/addressUnmodeled';
import { getAddressPlaceholder } from '../../../helpers/getPlacelholder';
import AddressStreet from './AddressStreet/AddressStreet';
import AddressZelenogradCity from './AddressZelenogradCity';
import { AddressFields } from '../../../../types/address';
import { ADDRESS_INFO_FIELD_NAME } from '../../../constants/address';

interface StateProps {
  street: AddressFields['street'];
  house: AddressFields['house'];
  structure: AddressFields['structure'];
  building: AddressFields['building'];
  isVisible: boolean;
  isNewMoscow: boolean;
  isLoading: boolean;
  isExpandedView?: boolean;
}

interface DispatchProps {
  onChange: ChangeEventHandler;
  onBlur: FocusEventHandler;
}

interface OwnProps {
  model: PersonModel;
}

type Props = OwnProps & StateProps & DispatchProps;

function AddressSubregionFields({
  model,
  isNewMoscow,
  isVisible,
  isExpandedView,
  isLoading,
  building,
  house,
  onChange,
  onBlur,
  street,
  structure,
}: Props): JSX.Element | null {
  if (!isVisible) return null;
  const disabled = isExpandedView || isLoading;
  return (
    <Fragment>
      <AddressZelenogradCity model={model} />
      {!isNewMoscow && (
        <AddressStreet
          model={model}
          disabled={disabled}
          value={street.value}
          error={street.error}
          placeholder={getAddressPlaceholder('street')}
        />
      )}
      {isNewMoscow && (
        <div className="PguFormGrid__row">
          <TextField
            className="PguFormGrid_col"
            placeholder={getAddressPlaceholder('street')}
            inputProps={{
              maxLength: 200,
              name: ADDRESS_INFO_FIELD_NAME.street,
              onChange,
              onBlur,
              ...street,
              disabled,
            }}
          />
        </div>
      )}
      <div className="PguFormGrid__row">
        <TextField
          className="PguFormGrid_col PguFormGrid__col_2"
          placeholder={getAddressPlaceholder('house')}
          maskOptions={{
            regex:
              '[А-Яа-яЁё0-9 \\-\\–\\—\\.\\,\\:\\;\\№\\«\\»\\“\\„"\\(\\) \\/]*',
          }}
          inputProps={{
            maxLength: 10,
            name: ADDRESS_INFO_FIELD_NAME.house,
            onChange,
            onBlur,
            ...house,
            disabled,
          }}
        />
        <TextField
          className="PguFormGrid_col PguFormGrid__col_2"
          placeholder={getAddressPlaceholder('structure')}
          inputProps={{
            maxLength: 10,
            name: ADDRESS_INFO_FIELD_NAME.structure,
            onChange,
            onBlur,
            ...structure,
            disabled,
          }}
        />
        <TextField
          className="PguFormGrid_col PguFormGrid__col_2"
          placeholder={getAddressPlaceholder('building')}
          inputProps={{
            maxLength: 10,
            name: ADDRESS_INFO_FIELD_NAME.building,
            onChange,
            onBlur,
            ...building,
            disabled,
          }}
        />
        <AddressFlat
          forManualInput
          isExpandedView={isExpandedView}
          model={model}
        />
      </div>
    </Fragment>
  );
}

const mapStateToProps = createStructuredSelector({
  isVisible: isAddressSubregionFieldsVisible,
  street: getStreet,
  house: getHouse,
  building: getBuilding,
  structure: getStructure,
  isLoading: isSubsequentRequestRunning,
  isNewMoscow,
});

function mapDispatchToProps(dispatch: Dispatch, { model }) {
  return {
    onChange({ target: { name, value } }) {
      dispatch({
        type: `${model}/address/change`,
        payload: { [name]: value },
      });
    },
    onBlur({ target: { name, value } }) {
      const trimmedValue = value.trim();
      dispatch({
        type: `${model}/address/change`,
        payload: { [name]: trimmedValue },
      });
      if (!model.includes('child')) {
        dispatch({ type: 'form/saveDraft' });
      }
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddressSubregionFields);
